// Client auth wiring (plan D10, revised by docs/plan/08-auth-worker.md). The
// SPA stores NOTHING: the session JWT lives in an httpOnly cookie set by the
// Cloudflare auth worker, and page JS only ever holds a copy in memory,
// fetched on demand for Convex's `setAuth`.
//
// The four page-side auth calls are re-exported from
// `@bindery/auth-consumer/browser` rather than written here. They used to be
// written here, and that is the bug the package exists to close: `signIn`
// minted the CSRF nonce, wrote the state cookie with `document.cookie`, and
// redirected to a broker origin THIS module had picked — while the worker
// exchanged the returned code against whatever its `vars` said. Two
// independent answers to one question, which agreed only by coincidence of
// configuration. `/auth/sign-in` is a worker route now, so the browser half
// knows no broker origin, no cookie name and no nonce format, and there is
// nothing left here for the worker to disagree with.
//
// Re-exported rather than imported directly by every caller so this module
// stays the app's one auth entry point: `src/main.tsx` and the components
// import from `@/lib/auth`, and the hooks and JWT decoding below sit
// alongside them.

import {
  type AuthState,
  createConvexAuthStore,
  useAuthState as useAuthStateIn,
  wireConvexAuth as wireConvexAuthIn,
} from "@bindery/auth-consumer/react";
import {
  sessionTypOf,
  USER_SESSION_TYP,
} from "@bindery/shared/contracts/session-typ";
import type { ConvexReactClient } from "convex/react";
import {
  consumedSessionPayload,
  consumedSessionPayloadSchema,
  type SessionPayload,
} from "@bindery/convex-retells/model/session";

import {
  consumeAuthErrorParam,
  fetchToken,
  signIn,
  signOut,
} from "@bindery/auth-consumer/browser";

export { consumeAuthErrorParam, fetchToken, signIn, signOut };
export type { AuthState };

// We mount a plain `ConvexProvider` (src/main.tsx), so `convex/react`'s
// `useConvexAuth` is unusable; `setAuth`'s onChange, mirrored into this
// store, is the substitute.
const authStore = createConvexAuthStore();

/**
 * Point a Convex client at the cookie-backed token source (call once at
 * boot). `onChange`, when supplied, is `setAuth`'s second argument — Convex
 * calls it with the resolved `isAuthenticated` state once the token fetch
 * settles; it's the designated trigger for a fresh sign-in's
 * `bookmarks.mergeLocal`.
 *
 * The same callback also feeds the module store behind `useIsAuthenticated`.
 */
export function wireConvexAuth(
  client: ConvexReactClient,
  onChange?: (isAuthenticated: boolean) => void,
): void {
  wireConvexAuthIn(client, authStore, onChange);
}

/**
 * Convex's view of the session, INCLUDING whether it has one yet. Starts
 * `"unknown"` and resolves as `setAuth`'s onChange fires.
 *
 * Use this — not `useIsAuthenticated` — anywhere the signed-out branch is
 * something a reader can SEE, because the two branches are not each other's
 * negation until the state settles. `AppNav` is the case that forced this: it
 * renders in the corner of every route on first paint, so collapsing
 * `"unknown"` into "anonymous" put a "Sign in" button in front of readers who
 * were already signed in, for the length of the boot token fetch.
 */
export function useAuthState(): AuthState {
  return useAuthStateIn(authStore);
}

/**
 * Whether Convex holds a CONFIRMED session — `false` covers both "no session"
 * and "not resolved yet". Fine for gating a query argument or a control that
 * only exists behind an interaction (by which time the state has settled);
 * see `useAuthState` for anything visible on first paint.
 */
export function useIsAuthenticated(): boolean {
  return useAuthState() === "authenticated";
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Decode the broker JWT's payload into the consumed subset
 * (`@bindery/convex-retells/model/session`, the pinned mirror of
 * `@bindery/shared/contracts/session`). This is a real base64/JSON boundary — a
 * malformed token must yield `null`, never throw — and it is NOT verification
 * (Convex verifies the signature); it only reads display/correlation claims
 * (e.g. `readerId` for logging).
 *
 * The three steps are the boundary, the contract, and the projection. Only the
 * first is written here: base64url + `JSON.parse` inside a `try`. The claims
 * are then `consumedSessionPayloadSchema`'s to judge — the contract's
 * `user_session` arm `.pick`ed to what this app reads, so an unread claim can
 * neither fail the decode nor survive it — and `consumedSessionPayload` does
 * the narrowing.
 *
 * Reading the CONSUMED claims through the contract rather than key by key
 * made this stricter in one direction: a claim this app reads that carries a
 * malformed value fails the decode instead of being dropped. A non-UUID
 * `readerId` used to yield a payload with that key quietly missing; it now
 * yields `null`, because the broker cannot mint one and a session identity
 * assembled out of a token we could not read is worse than no session. Two
 * things deliberately stay lenient. A claim this app never reads cannot refuse
 * a token — Convex verifies the signature itself and never runs this schema,
 * so refusing here would render a reader signed out of a page whose backend
 * still had them signed in. And an `internalRole` outside the contract's enum
 * is dropped, not refused: the enum is the broker's, and this app surfaces the
 * value without authorizing on it.
 */
export function decodeSessionPayload(jwt: string): SessionPayload | null {
  const parts = jwt.split(".");
  const [, payloadPart] = parts;
  if (parts.length !== 3 || payloadPart === undefined) return null;

  let raw: unknown;
  try {
    raw = JSON.parse(decodeBase64Url(payloadPart));
  } catch {
    return null;
  }
  if (typeof raw !== "object" || raw === null) return null;

  // Checked before the parse so a genuine reader_session is refused as the
  // wrong lane rather than as a malformed user one.
  if (sessionTypOf(raw) !== USER_SESSION_TYP) return null;

  const parsed = consumedSessionPayloadSchema.safeParse(raw);
  if (!parsed.success) return null;
  return consumedSessionPayload(parsed.data);
}
